/**
 * Token Pricing Service
 *
 * Centralized source of truth for all token pricing values.
 * Server-side: loads from database via PricingPolicyService
 * Client-side: uses default fallback values (DB not accessible from browser)
 *
 * NOTE: Client components should use /api/subscriptions/pricing endpoint
 * or useTokenPricing hook to get current pricing.
 */

import { PRICING_DEFAULTS } from '@/contexts/subscriptions/pricingDefaults';
import { logger } from '@/shared/infrastructure/logging/logger';

// Default fallback values for client-side usage (from single source of truth)
const DEFAULT_PRICING = {
  textPerK: PRICING_DEFAULTS.textPerK,
  audioPerK: PRICING_DEFAULTS.audioPerK,
  assemblyFixed: PRICING_DEFAULTS.assemblyFixed,
  rates: { ...PRICING_DEFAULTS.rates },
  reservationAmount: PRICING_DEFAULTS.storyReservation,
  blockTextReservation: PRICING_DEFAULTS.blockTextReservation,
  blockAudioReservation: PRICING_DEFAULTS.blockAudioReservation,
};

// Check if running on server
const isServer = typeof window === 'undefined';

/**
 * Token Pricing - Dynamic pricing loaded from database
 * All values are in tokens
 */
export class TokenPricing {
  /**
   * Get current pricing rates
   * Server: loads from database via PricingPolicyService
   * Client: returns default fallback values
   */
  static async getPricing() {
    // Client-side: return defaults (no DB access)
    if (!isServer) {
      return DEFAULT_PRICING;
    }

    // Server-side: load from database
    try {
      // Dynamic import to avoid bundling server code in client
      const { PricingPolicyService } = await import('./pricingPolicyService');
      const policy = await PricingPolicyService.getActivePolicy();
      return {
        // Legacy cost rates (tokens per 1000 characters)
        textPerK: policy.textPerK,
        audioPerK: policy.audioPerK,
        assemblyFixed: policy.assemblyFixed,
        // New structure
        rates: {
          llm: { tokensPer10K: policy.rates.llm.tokensPer10K },
          tts: { tokensPerK: policy.rates.tts.tokensPerK },
          assembly: { fixed: policy.rates.assembly.fixed },
          imagen: { tokensPerImage: policy.rates.imagen?.tokensPerImage ?? DEFAULT_PRICING.rates.imagen.tokensPerImage },
        },
        // Precharge reservations
        reservationAmount: policy.storyReservation,
        blockTextReservation: policy.blockTextReservation,
        blockAudioReservation: policy.blockAudioReservation,
      };
    } catch (error) {
      logger.error('Failed to load pricing from DB, using defaults:', error);
      return DEFAULT_PRICING;
    }
  }

  /**
   * Get story reservation amount (tokens reserved upfront)
   */
  static async getStoryReservation(): Promise<number> {
    const pricing = await this.getPricing();
    return pricing.reservationAmount;
  }

  /**
   * Calculate text generation cost
   *
   * @param llmTokens - Total LLM tokens used across all generation stages
   * @returns Token cost for text generation (minimum 1 token)
   *
   * Formula: ceil((llmTokens / 10000) * rate)
   * Examples with rate=1:
   * - 5000 tokens: ceil((5000/10000) * 1) = ceil(0.5) = 1 token
   * - 15000 tokens: ceil((15000/10000) * 1) = ceil(1.5) = 2 tokens
   * - 50000 tokens: ceil((50000/10000) * 1) = ceil(5) = 5 tokens
   */
  static async calculateTextCost(llmTokens: number): Promise<number> {
    const pricing = await this.getPricing();
    const rate = pricing.textPerK ?? pricing.rates?.llm?.tokensPer10K ?? 0;
    const exactCost = (llmTokens / 10000) * rate;
    const cost = Math.ceil(exactCost);
    // Minimum 1 token for any text generation
    return Math.max(1, cost);
  }

  /**
   * Calculate audio generation cost
   *
   * @param totalChars - Total characters to generate audio for
   * @returns Token cost for audio generation (minimum 1 token)
   *
   * Formula: ceil((chars / 1000) * rate)
   * Examples with rate=5:
   * - 100 chars: ceil((100/1000) * 5) = ceil(0.5) = 1 token
   * - 1200 chars: ceil((1200/1000) * 5) = ceil(6) = 6 tokens
   * - 5000 chars: ceil((5000/1000) * 5) = ceil(25) = 25 tokens
   */
  static async calculateAudioCost(totalChars: number): Promise<number> {
    const pricing = await this.getPricing();
    const rate = pricing.audioPerK ?? pricing.rates?.tts?.tokensPerK ?? 0;
    const exactCost = (totalChars / 1000) * rate;
    const cost = Math.ceil(exactCost);
    // Minimum 1 token for any audio generation
    return Math.max(1, cost);
  }

  /**
   * Get audio assembly cost (fixed)
   *
   * @returns Token cost for audio assembly (merging blocks)
   */
  static async getAssemblyCost(): Promise<number> {
    const pricing = await this.getPricing();
    return pricing.assemblyFixed ?? pricing.rates?.assembly?.fixed ?? 0;
  }

  /**
   * Calculate image generation cost
   *
   * @param imageCount - Number of images to generate
   * @returns Token cost for image generation
   *
   * Formula: imageCount * tokensPerImage
   * Default: 5 tokens per image
   * Examples with rate=5:
   * - 1 image: 1 * 5 = 5 tokens
   * - 4 images: 4 * 5 = 20 tokens
   * - 6 images: 6 * 5 = 30 tokens
   */
  static async calculateImageCost(imageCount: number): Promise<number> {
    const pricing = await this.getPricing();
    const rate = pricing.rates?.imagen?.tokensPerImage ?? DEFAULT_PRICING.rates.imagen.tokensPerImage;
    return imageCount * rate;
  }

  /**
   * Get image generation cost per image
   *
   * @returns Token cost per image
   */
  static async getImageCostPerImage(): Promise<number> {
    const pricing = await this.getPricing();
    return pricing.rates?.imagen?.tokensPerImage ?? DEFAULT_PRICING.rates.imagen.tokensPerImage;
  }

  /**
   * Get reservation amount for story creation
   * @deprecated Use UnifiedAIConfigService.getOperationReservation('story_pipeline')
   */
  static async getReservationAmount(): Promise<number> {
    if (isServer) {
      try {
        const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
        return UnifiedAIConfigService.getOperationReservation('story_pipeline');
      } catch { /* fallback below */ }
    }
    const pricing = await this.getPricing();
    return pricing.reservationAmount;
  }

  /**
   * Get text block operation reservation amount
   * @deprecated Use UnifiedAIConfigService.getOperationReservation('block_regeneration')
   */
  static async getBlockTextReservation(): Promise<number> {
    if (isServer) {
      try {
        const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
        return UnifiedAIConfigService.getOperationReservation('block_regeneration');
      } catch { /* fallback below */ }
    }
    const pricing = await this.getPricing();
    return pricing.blockTextReservation;
  }

  /**
   * Get audio block operation reservation amount
   * @deprecated Use UnifiedAIConfigService.getOperationReservation('audio_block_generation')
   */
  static async getBlockAudioReservation(): Promise<number> {
    if (isServer) {
      try {
        const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
        return UnifiedAIConfigService.getOperationReservation('audio_block_generation');
      } catch { /* fallback below */ }
    }
    const pricing = await this.getPricing();
    return pricing.blockAudioReservation;
  }

  /**
   * Get audio assembly operation cost (fixed)
   * @deprecated Assembly is free — returns 0
   */
  static async getAssemblyReservation(): Promise<number> {
    return 0;
  }

  /**
   * Calculate total cost for a complete story pipeline
   *
   * @param textChars - Total text characters
   * @param includeAudio - Whether audio generation is included
   * @param includeAssembly - Whether audio assembly is included
   * @param imageCount - Number of images to generate (optional)
   * @returns Total estimated cost breakdown
   */
  static async calculateTotalCost(params: {
    textChars: number;
    includeAudio?: boolean;
    includeAssembly?: boolean;
    imageCount?: number;
  }): Promise<{
    textCost: number;
    audioCost: number;
    assemblyCost: number;
    imageCost: number;
    total: number;
  }> {
    const { textChars, includeAudio = false, includeAssembly = false, imageCount = 0 } = params;

    const textCost = await this.calculateTextCost(textChars);
    const audioCost = includeAudio ? await this.calculateAudioCost(textChars) : 0;
    const assemblyCost = includeAssembly ? await this.getAssemblyCost() : 0;
    const imageCost = imageCount > 0 ? await this.calculateImageCost(imageCount) : 0;

    return {
      textCost,
      audioCost,
      assemblyCost,
      imageCost,
      total: textCost + audioCost + assemblyCost + imageCost,
    };
  }
}

/**
 * NOTE: Pricing is now loaded from database via PricingPolicyService.
 * Admin can update rates in /admin/billing/pricing without code changes.
 * Policy is cached for 30 seconds for performance.
 */
